import React from "react";
import { ListOrdered } from "lucide-react";
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import {
  Legislation,
  DocumentCategory,
} from "@/models/legislation/Legislation";
import { CustomColumnDef } from "@/components/data-table/types/custom-column-def";
import type { CustomColumnMeta } from "@/components/data-table/types/custom-column-meta";
import { FilterType } from "@/components/data-table/filters/config/filter-type";
import { CustomCellContext } from "@/components/data-table/types/custom-cell-context";
import FavoriteButton from "@/components/buttons/FavoriteButton";
import { cn } from "@/lib/utils";
import useFavoriteOperationStore from "@/stores/favorites.store";
import { dateValue } from "@/utils/legislationMetadataUtils.ts";
import DocumentLinkButton from "@/components/buttons/DocumentLinkButton";
import { ArvidMetadata } from "@/models/legislation/metadata/Arvid";
import {
  DataSource,
  normalizeDataSource,
  getInvalidLabel,
} from "@/config/datasources";
import SubscribeButton from "@/components/buttons/SubscribeButton";
import SubscribeAsGroupButton from "@/components/buttons/SubscribeAsGroupButton";
import {
  Subscription,
  SubscriptionCategory,
} from "@/models/subscriptions/Subscription";
import useSubscriptionOperationStore from "@/stores/subscription.store";
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";
import {
  type DynamicFilterDef,
  resolveValueFromDbColumn,
  humanizeWithMapping,
  renderCellValue,
} from "@/utils/metadataValueResolver";
import type { ColumnDataType } from "@/lib/getSortLabels";
import { getConsolidationProgressDisplay } from "@/lib/consolidationProgressDisplay";
import { getRetsinformationMetadataFromLegislation } from "@/lib/consolidationLegislationUtils";
import { isConsolidationEligibleDocumentType } from "@/utils/legislationDocumentTypeUtils";
import ConsolidationProgressBadge from "./ConsolidationProgressBadge";
// import SubscribeButton from "@/components/subscribe/SubscribeButton";

// Re-export DynamicFilterDef for backward compatibility
export type { DynamicFilterDef } from "@/utils/metadataValueResolver";

const sortDataTypeForDynamicFilter = (_f: DynamicFilterDef): ColumnDataType =>
  "text";

/**
 * Content-sized column. `width: 1px` on a table cell is the auto-table-layout
 * idiom for "no wider than the content": the column takes exactly the width of
 * its widest cell and every spare pixel goes to the one auto column, the title.
 */
const CONTENT_SIZED_COLUMN = {
  headerClassName: "w-px",
  cellClassName: "w-px",
} satisfies CustomColumnMeta;

/**
 * Free-text column (ministry, document type, …). Stays on one line while the
 * table is wide enough and wraps onto two lines once the table itself (the
 * `@container/table` around it) is narrower than 84rem — sidebar open on a 14"
 * laptop — instead of forcing the table to scroll sideways. Auto table layout
 * cannot express "wrap only once the title is at its minimum width", so the
 * switch is a container query rather than intrinsic sizing.
 */
const WRAPPING_TEXT_COLUMN = {
  headerClassName: "w-px",
  cellClassName: "w-px @max-[84rem]/table:whitespace-normal",
} satisfies CustomColumnMeta;

/** Shared geometry of the row-action bar and the in-flow slots reserved for
 *  it, so the reserved width always matches the rendered `size-9` buttons. */
const ROW_ACTION_BAR_CLASS = "flex flex-row gap-1 rounded-md border p-1";

const getArvidSummary = (row: Legislation): string | undefined => {
  const normalizedSource = normalizeDataSource(row.datasource);
  if (normalizedSource !== DataSource.Arvid) return undefined;

  if (!row.metadataObject) return undefined;

  let metaObj: Record<string, unknown> | undefined;
  if (typeof row.metadataObject === "string") {
    try {
      const parsed = JSON.parse(row.metadataObject);
      if (parsed && typeof parsed === "object") {
        metaObj = parsed as Record<string, unknown>;
      }
    } catch {
      return undefined;
    }
  } else if (row.metadataObject && typeof row.metadataObject === "object") {
    metaObj = row.metadataObject as Record<string, unknown>;
  }

  if (!metaObj) return undefined;

  const arvidMetadata = metaObj as ArvidMetadata;
  if (!arvidMetadata.summary) return undefined;

  return arvidMetadata.summary.trim() || undefined;
};

/** Parse metadataObject JSON and return documentTypeId (10 = Lov, 30 = Lovbekendtgørelse, 60 = Bekendtgørelse). Accepts string or number in JSON (e.g. "10", "30", "60"). */
function parseDocumentTypeIdFromMetadataObject(
  metadataObject: string | undefined
): number | undefined {
  if (!metadataObject || typeof metadataObject !== "string") return undefined;
  try {
    const parsed = JSON.parse(metadataObject) as {
      documentTypeId?: string | number;
    };
    const raw = parsed?.documentTypeId;
    if (raw == null) return undefined;
    const num = Number(raw);
    return Number.isNaN(num) ? undefined : num;
  } catch {
    return undefined;
  }
}

/** Set of "datasource/externalId" for legislations that any of the current user's groups is subscribed to. */
export const getLegislationColumns = (
  favoriteLegislationIds: string[],
  extras?: CustomColumnDef<Legislation>[],
  subscriptions: Subscription[] = [],
  groupLegislationSubscriptionKeys?: Set<string>,
  hasCreateFeatureGroups = false,
  showConsolidationProgress = false,
  onPrioritizeConsolidation?: (item: Legislation) => void
): CustomColumnDef<Legislation>[] => [
  {
    accessorKey: "title",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Titel" />
    ),
    meta: {
      displayName: "Titel",
      dataType: "text",
    },
    cell: ({ row, isHovering = false }: CustomCellContext<Legislation>) => {
      const title = row.original.title;
      const isFavorite = favoriteLegislationIds.includes(
        row.original.externalId
      );
      const isFavoriteLoading = useFavoriteOperationStore
        .getState()
        .isPending(row.original.externalId);
      const arvidSummary = getArvidSummary(row.original);

      // Build entityPath: legislation/<external_id>/datasource/<datasource>
      const entityPath = `legislation/${row.original.externalId}/datasource/${row.original.datasource}`;

      const subscribedEntry = Array.isArray(subscriptions)
        ? subscriptions.find((s) => s.entityPath === entityPath)
        : undefined;
      const isSubscribed = !!subscribedEntry;
      const isGroupSubscribed = !!groupLegislationSubscriptionKeys?.has(
        `${row.original.datasource}/${row.original.externalId}`
      );
      const store = useSubscriptionOperationStore.getState();
      const isSubscribedLoading =
        store.isPending(entityPath) ||
        (!!subscribedEntry?.id &&
          store.isPending(`legislation-${subscribedEntry.id}`));
      const documentTypeId = parseDocumentTypeIdFromMetadataObject(
        row.original.metadataObject
      );
      const legislationMetadata = {
        datasource: row.original.datasource,
        legislationExternalId: row.original.legislationExternalId,
        publishedOn: row.original.publishedOn,
        isCurrent: row.original.isCurrent,
        documentTypeId,
      };

      // The toggles double as indicators: an active one (subscribed,
      // favourited, or mid-request) stays visible when the row is not hovered.
      // Only those get in-flow space next to the title; the hover-only actions
      // are drawn on top of the title instead, so hovering never changes the
      // title's width, never re-wraps it and never changes the row height.
      const showsSubscribed = isSubscribed || isSubscribedLoading;
      const showsFavorite = isFavorite || isFavoriteLoading;
      const toggles = [
        {
          key: "subscribe",
          active: showsSubscribed,
          node: (isHovering || showsSubscribed) && (
            <SubscribeButton
              subscribeCategory={SubscriptionCategory.Legislation}
              entityPath={entityPath}
              entityName={title}
              source="legislation"
              legislationExternalId={row.original.externalId}
              legislationDatasource={row.original.datasource}
              legislationSubscriptionId={subscribedEntry?.id}
              legislationMetadata={legislationMetadata}
              isSubscribed={isSubscribed}
            />
          ),
        },
        {
          key: "group",
          active: isGroupSubscribed,
          node: (isGroupSubscribed ||
            (isHovering && hasCreateFeatureGroups)) && (
            <SubscribeAsGroupButton
              subscribeCategory={SubscriptionCategory.Legislation}
              entityPath={entityPath}
              entityName={title}
              source="legislation"
              legislationExternalId={row.original.externalId}
              legislationDatasource={row.original.datasource}
              isGroupSubscribed={isGroupSubscribed}
              legislationMetadata={legislationMetadata}
            />
          ),
        },
        {
          key: "favorite",
          active: showsFavorite,
          // Renders itself only while hovered, favourited or pending.
          node: (
            <FavoriteButton
              isHovering={isHovering}
              documentCategory={DocumentCategory.Legislation}
              documentId={row.original.externalId}
              title={row.original.title}
              isFavorite={isFavorite}
              datasource={row.original.datasource}
              internalId={row.original.internalId}
            />
          ),
        },
      ];
      // Active indicators sink to the right end of the bar so they sit exactly
      // over the reserved in-flow slots and stay put when the hover-only
      // actions appear to their left.
      const orderedToggles = [
        ...toggles.filter((toggle) => !toggle.active),
        ...toggles.filter((toggle) => toggle.active),
      ];
      const persistentCount = toggles.filter((toggle) => toggle.active).length;

      return (
        <div className="relative flex min-w-[200px] flex-1 items-center gap-2 py-1">
          <div className="min-w-0 flex-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <p className="line-clamp-3 whitespace-normal break-words">
                  {title}
                </p>
              </TooltipTrigger>
              <TooltipContent className="max-w-md">
                <div className="space-y-2">
                  <p className="font-medium wrap-break-word">{title}</p>
                  {arvidSummary && (
                    <div
                      className="text-sm wrap-break-word"
                      dangerouslySetInnerHTML={{ __html: arvidSummary }}
                    />
                  )}
                </div>
              </TooltipContent>
            </Tooltip>
          </div>
          {persistentCount > 0 && (
            <div
              aria-hidden
              className={cn(
                ROW_ACTION_BAR_CLASS,
                "shrink-0 border-transparent"
              )}
            >
              {Array.from({ length: persistentCount }, (_, index) => (
                <span key={index} className="w-9" />
              ))}
            </div>
          )}
          <div
            className={cn(
              ROW_ACTION_BAR_CLASS,
              "absolute right-0 top-1/2 -translate-y-1/2 border-border bg-background shadow-md",
              !(isHovering || persistentCount > 0) && "hidden"
            )}
          >
            <DocumentLinkButton
              isHovering={isHovering}
              documentId={row.original.externalId}
              source={row.original.datasource}
            />
            {onPrioritizeConsolidation &&
              isHovering &&
              normalizeDataSource(row.original.datasource) ===
                DataSource.Retsinformation &&
              isConsolidationEligibleDocumentType(documentTypeId) && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="p-2"
                      aria-label={`Prioriter konsolidering af ${title}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onPrioritizeConsolidation(row.original);
                      }}
                    >
                      <ListOrdered className="size-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Prioriter konsolidering</TooltipContent>
                </Tooltip>
              )}
            {orderedToggles.map((toggle) => (
              <React.Fragment key={toggle.key}>{toggle.node}</React.Fragment>
            ))}
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "datasource",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Kilde" />
    ),
    cell: ({ row }: CustomCellContext<Legislation>) => {
      const source = row.original.datasource;
      return source.charAt(0).toUpperCase() + source.slice(1);
    },
    meta: {
      ...CONTENT_SIZED_COLUMN,
      filterType: FilterType.MultiSelect,
      displayName: "Kilde",
      dataType: "text",
    },
  },
  {
    accessorKey: "legislationExternalId",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Nummer" />
    ),
    cell: ({ row }: CustomCellContext<Legislation>) => {
      const legislationExternalId = row.original.legislationExternalId;
      return legislationExternalId || "-";
    },
    meta: {
      ...CONTENT_SIZED_COLUMN,
      displayName: "Nummer",
      databaseColumn: "legislation_external_id",
      dataType: "text",
    },
  },
  {
    accessorKey: "publishedOn",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Dato" />
    ),
    cell: ({ row }: CustomCellContext<Legislation>) => {
      const publishedOn = row.original.publishedOn;
      return publishedOn ? dateValue(publishedOn) : "-";
    },
    meta: {
      ...CONTENT_SIZED_COLUMN,
      displayName: "Dato",
      databaseColumn: "published_on",
      dataType: "date",
    },
  },
  {
    accessorKey: "isCurrent",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }: CustomCellContext<Legislation>) => {
      const isCurrent = row.original.isCurrent;
      if (isCurrent === null || isCurrent === undefined) {
        return "-";
      }
      return isCurrent ? "Gældende" : getInvalidLabel(row.original.datasource);
    },
    meta: {
      ...CONTENT_SIZED_COLUMN,
      displayName: "Status",
      databaseColumn: "is_current",
      dataType: "text",
    },
  },
  {
    // Hidden column for status filter - filter UI requires a column with accessorKey matching filter key
    accessorKey: "status",
    accessorFn: (row: Legislation) => {
      const isCurrent = row.isCurrent;
      if (isCurrent === null || isCurrent === undefined) {
        return null;
      }
      return isCurrent ? "Gældende" : "Ikke-gældende";
    },
    header: () => null,
    cell: () => null,
    enableHiding: false,
    enableSorting: false,
    enableColumnFilter: true,
    meta: {
      displayName: "Status",
      databaseColumn: "is_current",
    },
  },
  ...(showConsolidationProgress
    ? [
        {
          id: "consolidationProgress",
          // Not sortable: the table sorts server-side (manualSorting) and this
          // value is computed from per-row metadata with no database column.
          enableSorting: false,
          header: ({ column }) => (
            <DataTableColumnHeader column={column} title="Konsolidering" />
          ),
          cell: ({ row }: CustomCellContext<Legislation>) => {
            const meta = getRetsinformationMetadataFromLegislation(
              row.original
            );
            const display = getConsolidationProgressDisplay({
              inboundChanges: meta?.inboundChanges,
              timelineAmendmentCountAfter: row.original.timelineAmendmentCountAfter,
              documentTypeId: meta?.documentTypeId,
            });
            if (!display) return "-";
            return <ConsolidationProgressBadge display={display} />;
          },
          meta: {
            ...CONTENT_SIZED_COLUMN,
            displayName: "Konsolidering",
            dataType: "number" as const,
          },
        } satisfies CustomColumnDef<Legislation>,
      ]
    : []),
  ...(extras ?? []),
];

export const buildDynamicColumns = (
  defs: DynamicFilterDef[]
): CustomColumnDef<Legislation>[] =>
  defs.map((f) => {
    const dataType = sortDataTypeForDynamicFilter(f);
    return {
    accessorKey: f.key,
    accessorFn: (row: Legislation) => {
      return resolveValueFromDbColumn(row, f);
    },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={f.displayName ?? f.key} />
    ),
    meta: {
      ...WRAPPING_TEXT_COLUMN,
      displayName: f.displayName ?? f.key,
      databaseColumn: f.databaseColumn ?? f.key,
      dataType,
    },
    cell: ({
      row,
    }: CustomCellContext<Legislation>): React.ReactNode => {
      const raw = resolveValueFromDbColumn(row.original, f);
      const human = humanizeWithMapping(raw, f.valueMapping);

      const humanStr =
        typeof human === "string" && human.length > 0
          ? human.charAt(0).toUpperCase() + human.slice(1)
          : human;
      return renderCellValue(humanStr);
    },
  };
  });
